/**
 * Settings Module
 * Handles user preferences, profile management, and app configuration
 */

import { getCurrentUser, sendPasswordReset } from './auth.js';
import { db } from './firebase.js';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { captureBudgetData, getBudgetVersions, loadBudgetVersionData, calculateDataTotals, getCurrentVersionName, convertToMonthlyAmount, renderDonutChartToElement, setAutoSaveFrequency } from './budget.js';
import { jsPDF } from 'jspdf';
import { redirectToCheckout, openBillingPortal, getMembershipStatus, getDaysRemainingInTrial, getMembershipData } from './subscription.js';

/**
 * Settings State
 */
let settingsState = {
  currentSection: 'profile',
  userSettings: null,
  isDirty: false, // Tracks if settings have been modified
  autoSaveTimeout: null
};

/**
 * Default Settings Structure
 */
const defaultSettings = {
  // Profile Information
  displayName: '',
  phone: '',
  timezone: 'auto',
  currency: 'USD',
  dateFormat: 'MM/DD/YYYY',
  profilePictureUrl: '',
  
  // Notification Preferences
  notifications: {
    pushEnabled: true,
    emailEnabled: true,
    smsEnabled: false,
    quietHours: {
      enabled: false,
      startTime: '22:00',
      endTime: '08:00'
    },
    budgetAlerts: {
      enabled: true,
      thresholds: [80, 90, 100]
    },
    breakdownEnabled: true,
    breakdownMethod: 'email',
    breakdownFrequency: 'weekly',
    sharingAlerts: true,
    marketingOptIn: false
  },
  
  // App Preferences
  interface: {
    theme: 'system',
    accentColor: '#667eea',
    fontSize: 'medium',
    layoutDensity: 'spacious',
    defaultViewDesktop: 'budget',
    defaultViewMobile: 'expenses',
    highContrast: false,
    reducedMotion: false
  },
  
  // Privacy Settings
  privacy: {
    contactVisibility: 'collaborators',
    activityVisibility: 'collaborators',
    profileVisibility: 'collaborators',
    analyticsOptOut: false,
    marketingOptOut: false,
    crashReporting: true
  },
  
  // Sharing Preferences
  sharing: {
    defaultPermissionLevel: 'contribute',
    allowDirectInvites: true,
    requireApprovalForSharing: false,
    autoAcceptFromDomains: []
  },
  
  // App Behavior
  behavior: {
    confirmDelete: true,
    confirmLargeExpenses: true,
    largeExpenseThreshold: 100,
    autoSaveFrequency: 5, // seconds
    offlineMode: true
  },
  
  // Metadata
  createdAt: new Date(),
  updatedAt: new Date()
};

/**
 * Ensure correct DOM structure for settings
 * Fix issue where settings-mode gets nested inside other mode containers
 */
function ensureCorrectDOMStructure() {
  const settingsMode = document.getElementById('settings-mode');
  const content = document.querySelector('.content');
  
  if (!settingsMode || !content) return;
  
  // Check if settings-mode is properly positioned as direct child of content
  if (settingsMode.parentElement !== content) {
    console.log('Fixing settings DOM structure - moving to correct parent');
    
    // Remove from current position
    settingsMode.remove();
    
    // Add as direct child of content container
    content.appendChild(settingsMode);
    
    console.log('Settings DOM structure fixed');
  }
}

/**
 * Initialize Settings Module
 */
export async function initializeSettings() {
  console.log('Initializing settings module...');
  
  try {
    // Fix DOM structure - ensure settings-mode is not nested inside other mode containers
    ensureCorrectDOMStructure();
    // Set up event listeners
    setupSettingsNavigation();
    setupFormHandlers();
    
    // Household sharing replaced by link sharing - no initialization needed
    
    // Load user settings if authenticated
    const user = getCurrentUser();
    if (user) {
      await loadUserSettings(user.uid);
    }
    
    console.log('Settings module initialized successfully');
  } catch (error) {
    console.error('Failed to initialize settings:', error);
  }
}

/**
 * Setup Settings Navigation
 */
function setupSettingsNavigation() {
  const navItems = document.querySelectorAll('.settings-nav-item');
  const sections = document.querySelectorAll('.settings-section');
  
  navItems.forEach(item => {
    item.addEventListener('click', (e) => {
      e.preventDefault();
      const sectionId = item.dataset.section;
      switchToSection(sectionId);
    });
  });
}

/**
 * Switch to Settings Section
 */
async function switchToSection(sectionId) {
  // Update navigation
  document.querySelectorAll('.settings-nav-item').forEach(item => {
    item.classList.remove('active');
  });
  document.querySelector(`[data-section="${sectionId}"]`).classList.add('active');

  // Update content
  document.querySelectorAll('.settings-section').forEach(section => {
    section.classList.remove('active');
  });
  document.getElementById(`${sectionId}-section`).classList.add('active');

  settingsState.currentSection = sectionId;

  // Refresh subscription UI when switching to billing section
  if (sectionId === 'billing') {
    await updateSubscriptionUI();
  }
}

/**
 * Setup Form Handlers
 */
function setupFormHandlers() {
  // Get all form inputs
  const inputs = document.querySelectorAll('.settings-content input, .settings-content select');
  
  inputs.forEach(input => {
    input.addEventListener('change', handleSettingChange);
    input.addEventListener('input', handleSettingChange);
  });
  
  // Setup specific toggle switch handlers
  setupToggleSwitches();
  
  // Setup button handlers
  setupButtonHandlers();
}

/**
 * Setup Toggle Switch Handlers
 */
function setupToggleSwitches() {
  const toggleContainers = document.querySelectorAll('.toggle-switch');
  
  toggleContainers.forEach(container => {
    const checkbox = container.querySelector('input[type="checkbox"]');
    const slider = container.querySelector('.toggle-slider');
    
    if (checkbox && slider) {
      // Handle clicks on the slider (visual element)
      slider.addEventListener('click', (e) => {
        e.preventDefault();
        checkbox.checked = !checkbox.checked;
        console.log(`Toggle clicked: ${checkbox.id} = ${checkbox.checked}`);
        
        // Trigger change event manually
        const changeEvent = new Event('change', { bubbles: true });
        checkbox.dispatchEvent(changeEvent);
      });
      
      // Handle change events on the actual checkbox
      checkbox.addEventListener('change', (e) => {
        console.log(`Toggle changed: ${e.target.id} = ${e.target.checked}`);
        handleSettingChange(e);
      });
    }
  });
}

/**
 * Handle Setting Change
 */
function handleSettingChange(e) {
  const { target } = e;
  const { id, type, value, checked } = target;
  
  if (!settingsState.userSettings) return;
  
  // Update the settings object based on the input
  updateSettingValue(id, type === 'checkbox' ? checked : value);
  
  // Mark as dirty and schedule auto-save
  settingsState.isDirty = true;
  scheduleAutoSave();
  
  // Update UI to show unsaved changes
  updateSaveStatus('unsaved');
}

/**
 * Update Setting Value in State
 */
function updateSettingValue(settingId, value) {
  const settings = settingsState.userSettings;
  
  // Map setting IDs to settings object paths
  const settingMappings = {
    'display-name': () => settings.displayName = value,
    'phone-number': () => settings.phone = value,
    'currency-preference': () => settings.currency = value,
    'timezone': () => settings.timezone = value,
    'breakdown-enabled': () => settings.notifications.breakdownEnabled = value,
    'breakdown-method': () => settings.notifications.breakdownMethod = value,
    'breakdown-frequency': () => settings.notifications.breakdownFrequency = value,
    'budget-alerts-enabled': () => settings.notifications.budgetAlerts.enabled = value,
    'threshold-80': () => updateThreshold(80, value),
    'threshold-90': () => updateThreshold(90, value),
    'threshold-100': () => updateThreshold(100, value),
    'quiet-hours-enabled': () => settings.notifications.quietHours.enabled = value,
    'quiet-start': () => settings.notifications.quietHours.startTime = value,
    'quiet-end': () => settings.notifications.quietHours.endTime = value,
    'theme-light': () => value && (settings.interface.theme = 'light'),
    'theme-dark': () => value && (settings.interface.theme = 'dark'),
    'theme-system': () => value && (settings.interface.theme = 'system'),
    'font-size': () => settings.interface.fontSize = value,
    'layout-density': () => settings.interface.layoutDensity = value,
    'high-contrast': () => settings.interface.highContrast = value,
    'reduced-motion': () => settings.interface.reducedMotion = value,
    'default-permission': () => settings.sharing.defaultPermissionLevel = value,
    'require-approval': () => settings.sharing.requireApprovalForSharing = value,
    'contact-visibility': () => settings.privacy.contactVisibility = value,
    'activity-visibility': () => settings.privacy.activityVisibility = value,
    'analytics-opt-out': () => settings.privacy.analyticsOptOut = value,
    'marketing-opt-out': () => settings.privacy.marketingOptOut = value,
    'crash-reporting': () => settings.privacy.crashReporting = value,
    'default-view-desktop': () => settings.interface.defaultViewDesktop = value,
    'default-view-mobile': () => settings.interface.defaultViewMobile = value,
    'confirm-delete': () => settings.behavior.confirmDelete = value,
    'confirm-large-expenses': () => settings.behavior.confirmLargeExpenses = value,
    'auto-save': () => {
      const freq = (value === 'manual' || value === 'immediate') ? value : (parseInt(value) || 30);
      settings.behavior.autoSaveFrequency = freq;
      setAutoSaveFrequency(String(freq));
    },
    'offline-mode': () => settings.behavior.offlineMode = value
  };
  
  const updateFunction = settingMappings[settingId];
  if (updateFunction) {
    updateFunction();
    settings.updatedAt = new Date();
  }
}

/**
 * Update Budget Alert Threshold
 */
function updateThreshold(threshold, enabled) {
  const settings = settingsState.userSettings;
  const thresholds = settings.notifications.budgetAlerts.thresholds;
  
  if (enabled && !thresholds.includes(threshold)) {
    thresholds.push(threshold);
    thresholds.sort((a, b) => a - b);
  } else if (!enabled && thresholds.includes(threshold)) {
    const index = thresholds.indexOf(threshold);
    thresholds.splice(index, 1);
  }
}

/**
 * Setup Button Handlers
 */
function setupButtonHandlers() {
  // Save Settings Button
  const saveBtn = document.getElementById('save-settings');
  if (saveBtn) {
    saveBtn.addEventListener('click', saveSettings);
  }
  
  // Reset Settings Button
  const resetBtn = document.getElementById('reset-settings');
  if (resetBtn) {
    resetBtn.addEventListener('click', resetSettings);
  }
  
  // Export buttons
  setupExportButtons();
  
  // Other action buttons
  setupActionButtons();
}

/**
 * Setup Export Buttons
 */
function setupExportButtons() {
  const toISO = d => d.toISOString().slice(0, 10);

  const setupDateToggle = (btnId, rangeId, fromId, toId, onDownload) => {
    const btn = document.getElementById(btnId);
    const range = document.getElementById(rangeId);
    const fromInput = document.getElementById(fromId);
    const toInput = document.getElementById(toId);
    if (!btn || !range) return;

    const today = new Date();
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(today.getDate() - 60);

    btn.addEventListener('click', () => {
      const visible = range.style.display !== 'none';
      if (!visible) {
        fromInput.value = toISO(sixtyDaysAgo);
        toInput.value = toISO(today);
      }
      range.style.display = visible ? 'none' : 'flex';
    });

    range.querySelector('button')?.addEventListener('click', () => {
      onDownload(fromInput.value, toInput.value);
    });
  };

  setupDateToggle('export-json-btn', 'json-date-range', 'json-date-from', 'json-date-to',
    (from, to) => handleDownloadData(from, to));

  setupDateToggle('export-csv-btn', 'csv-date-range', 'csv-date-from', 'csv-date-to',
    (from, to) => exportExpensesCSV(from, to));

  // PDF has no date range
  document.querySelectorAll('.export-options .settings-btn').forEach(btn => {
    if (btn.textContent.includes('PDF')) {
      btn.addEventListener('click', () => exportUserData('pdf'));
    }
  });
}

/**
 * Setup Action Buttons
 */
function setupActionButtons() {
  // Helper function to find button by text content
  function findButtonByText(text) {
    const buttons = document.querySelectorAll('.settings-content button');
    return Array.from(buttons).find(button => 
      button.textContent.trim().includes(text)
    );
  }
  
  // Change Password
  const changePasswordBtn = findButtonByText('Change Password');
  if (changePasswordBtn) {
    changePasswordBtn.addEventListener('click', handleChangePassword);
  }
  
  // Manage Household Sharing (now handled in hamburger menu)
  // const manageHouseholdBtn = document.getElementById('manage-household-btn');
  // if (manageHouseholdBtn) {
  //   manageHouseholdBtn.addEventListener('click', handleManageSharedBudgets);
  // }
  
  // Delete Account
  const deleteAccountBtn = findButtonByText('Delete My Account');
  if (deleteAccountBtn) {
    deleteAccountBtn.addEventListener('click', handleDeleteAccount);
  }

  // Subscription Management - Upgrade to Premium
  const upgradeBtn = document.getElementById('upgrade-premium-btn');
  if (upgradeBtn) {
    upgradeBtn.addEventListener('click', () => redirectToCheckout(upgradeBtn));
  }

  // Subscription Management - Manage Subscription
  const manageBillingBtn = document.getElementById('manage-billing-btn');
  if (manageBillingBtn) {
    manageBillingBtn.addEventListener('click', () => openBillingPortal(manageBillingBtn));
  }
}


/**
 * Load User Settings
 */
export async function loadUserSettings(userId) {
  try {
    updateSaveStatus('loading');
    
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (userDoc.exists() && userDoc.data().settings) {
      settingsState.userSettings = { ...defaultSettings, ...userDoc.data().settings };
    } else {
      // Create default settings for new user
      settingsState.userSettings = { ...defaultSettings };
      await setDoc(doc(db, 'users', userId), { 
        settings: settingsState.userSettings 
      }, { merge: true });
    }
    
    // Populate the form with loaded settings
    populateSettingsForm();

    // Update subscription UI
    await updateSubscriptionUI();

    updateSaveStatus('saved');

  } catch (error) {
    console.error('Error loading user settings:', error);
    updateSaveStatus('error', 'Failed to load settings');

    // Use default settings as fallback
    settingsState.userSettings = { ...defaultSettings };
    populateSettingsForm();
  }
}

/**
 * Update Subscription UI based on membership status.
 * Reads from the subscription module's active listener so linked users
 * automatically reflect the owner's membership instead of their own.
 */
async function updateSubscriptionUI() {
  const membership = getMembershipData();
  const status = membership.status || 'trial';

  // Update UI elements
  const planNameEl = document.getElementById('current-plan-name');
  const planStatusEl = document.getElementById('current-plan-status');
  const detailTextEl = document.getElementById('subscription-detail-text');
  const daysRemainingEl = document.getElementById('days-remaining');
  const upgradeBtn = document.getElementById('upgrade-premium-btn');
  const manageBillingBtn = document.getElementById('manage-billing-btn');

  if (status === 'trial') {
    // Calculate days remaining
    const trialExpiry = membership.trialExpiry
      ? (typeof membership.trialExpiry.toDate === 'function' ? membership.trialExpiry.toDate() : new Date(membership.trialExpiry))
      : null;

    const now = new Date();
    const daysRemaining = trialExpiry ? Math.ceil((trialExpiry - now) / (1000 * 60 * 60 * 24)) : 30;

    // Update UI
    if (planNameEl) planNameEl.textContent = 'Free Trial';
    if (planStatusEl) planStatusEl.textContent = 'Active';
    if (daysRemainingEl) daysRemainingEl.textContent = daysRemaining;
    if (detailTextEl) detailTextEl.innerHTML = `Your trial expires in <span id="days-remaining">${daysRemaining}</span> days`;

    // Show upgrade button, hide manage button
    if (upgradeBtn) upgradeBtn.style.display = 'block';
    if (manageBillingBtn) manageBillingBtn.style.display = 'none';

  } else if (status === 'premium') {
    // Premium user
    if (planNameEl) planNameEl.textContent = 'Premium';
    if (planStatusEl) planStatusEl.textContent = 'Active';

    // Show renewal date if available
    const rawPeriodEnd = membership.currentPeriodEnd;
    const periodEnd = rawPeriodEnd?.toDate?.() ?? (rawPeriodEnd ? new Date(rawPeriodEnd) : null);
    if (periodEnd && detailTextEl) {
      const renewDate = periodEnd.toLocaleDateString();
      detailTextEl.textContent = `Your subscription renews on ${renewDate}`;
    } else if (detailTextEl) {
      detailTextEl.textContent = 'You have full access to all premium features';
    }

    // Show manage button, hide upgrade button
    if (upgradeBtn) upgradeBtn.style.display = 'none';
    if (manageBillingBtn) manageBillingBtn.style.display = 'block';

  } else if (status === 'expired' || status === 'past_due') {
    // Expired or payment failed
    if (planNameEl) planNameEl.textContent = status === 'past_due' ? 'Payment Issue' : 'Expired';
    if (planStatusEl) planStatusEl.textContent = status === 'past_due' ? 'Action Required' : 'Inactive';
    if (detailTextEl) {
      detailTextEl.textContent = status === 'past_due'
        ? 'Please update your payment method to continue'
        : 'Your trial has expired. Upgrade to continue using BudgetApp';
    }

    // Show upgrade button
    if (upgradeBtn) upgradeBtn.style.display = 'block';
    if (manageBillingBtn) manageBillingBtn.style.display = status === 'past_due' ? 'block' : 'none';
  }
}

/**
 * Populate Settings Form
 */
function populateSettingsForm() {
  if (!settingsState.userSettings) return;
  
  const settings = settingsState.userSettings;
  
  // Basic Information
  setInputValue('display-name', settings.displayName);
  setInputValue('phone-number', settings.phone);
  setInputValue('currency-preference', settings.currency);
  setInputValue('timezone', settings.timezone);
  
  // Notifications
  setInputValue('breakdown-enabled', settings.notifications.breakdownEnabled);
  setInputValue('breakdown-method', settings.notifications.breakdownMethod);
  setInputValue('breakdown-frequency', settings.notifications.breakdownFrequency);
  setInputValue('budget-alerts-enabled', settings.notifications.budgetAlerts.enabled);
  
  // Threshold checkboxes
  setInputValue('threshold-80', settings.notifications.budgetAlerts.thresholds.includes(80));
  setInputValue('threshold-90', settings.notifications.budgetAlerts.thresholds.includes(90));
  setInputValue('threshold-100', settings.notifications.budgetAlerts.thresholds.includes(100));
  
  // Quiet hours
  setInputValue('quiet-hours-enabled', settings.notifications.quietHours.enabled);
  setInputValue('quiet-start', settings.notifications.quietHours.startTime);
  setInputValue('quiet-end', settings.notifications.quietHours.endTime);
  
  // Theme
  const themeRadio = document.querySelector(`input[name="theme"][value="${settings.interface.theme}"]`);
  if (themeRadio) themeRadio.checked = true;
  
  // Interface
  setInputValue('font-size', settings.interface.fontSize);
  setInputValue('layout-density', settings.interface.layoutDensity);
  setInputValue('high-contrast', settings.interface.highContrast);
  setInputValue('reduced-motion', settings.interface.reducedMotion);
  
  // Sharing
  setInputValue('default-permission', settings.sharing.defaultPermissionLevel);
  setInputValue('require-approval', settings.sharing.requireApprovalForSharing);
  
  // Privacy
  setInputValue('contact-visibility', settings.privacy.contactVisibility);
  setInputValue('activity-visibility', settings.privacy.activityVisibility);
  setInputValue('analytics-opt-out', settings.privacy.analyticsOptOut);
  setInputValue('marketing-opt-out', settings.privacy.marketingOptOut);
  setInputValue('crash-reporting', settings.privacy.crashReporting);
  
  // Behavior
  setInputValue('default-view-desktop', settings.interface.defaultViewDesktop);
  setInputValue('default-view-mobile', settings.interface.defaultViewMobile);
  setInputValue('confirm-delete', settings.behavior.confirmDelete);
  setInputValue('confirm-large-expenses', settings.behavior.confirmLargeExpenses);
  setInputValue('auto-save', settings.behavior.autoSaveFrequency.toString());
  setAutoSaveFrequency(settings.behavior.autoSaveFrequency.toString());
  setInputValue('offline-mode', settings.behavior.offlineMode);
  
  // Set email address (readonly from auth)
  const user = getCurrentUser();
  if (user) {
    setInputValue('email-address', user.email);
  }
}

/**
 * Helper: Set Input Value
 */
function setInputValue(id, value) {
  const element = document.getElementById(id);
  if (!element) return;
  
  if (element.type === 'checkbox') {
    element.checked = value;
  } else {
    element.value = value || '';
  }
}

/**
 * Schedule Auto-Save
 */
function scheduleAutoSave() {
  if (settingsState.autoSaveTimeout) {
    clearTimeout(settingsState.autoSaveTimeout);
  }
  
  const frequency = settingsState.userSettings?.behavior?.autoSaveFrequency || 30;
  if (frequency === 'manual') return;
  
  settingsState.autoSaveTimeout = setTimeout(() => {
    if (settingsState.isDirty) {
      saveSettings(true); // Auto-save
    }
  }, (frequency * 1000));
}

/**
 * Save Settings
 */
export async function saveSettings(isAutoSave = false) {
  if (!settingsState.userSettings || !settingsState.isDirty) return;
  
  const user = getCurrentUser();
  if (!user) {
    updateSaveStatus('error', 'Not authenticated');
    return;
  }
  
  try {
    updateSaveStatus('saving');
    
    settingsState.userSettings.updatedAt = new Date();
    
    await setDoc(doc(db, 'users', user.uid), { 
      settings: settingsState.userSettings 
    }, { merge: true });
    
    settingsState.isDirty = false;
    updateSaveStatus('saved', isAutoSave ? 'Auto-saved' : 'Settings saved');
    
    // Apply certain settings immediately
    applyImmediateSettings();
    
  } catch (error) {
    console.error('Error saving settings:', error);
    updateSaveStatus('error', 'Failed to save settings');
  }
}

/**
 * Reset Settings to Defaults
 */
function resetSettings() {
  if (!confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.')) {
    return;
  }
  
  settingsState.userSettings = { ...defaultSettings };
  populateSettingsForm();
  settingsState.isDirty = true;
  updateSaveStatus('unsaved');
}

/**
 * Apply Immediate Settings
 */
function applyImmediateSettings() {
  const settings = settingsState.userSettings;
  if (!settings) return;
  
  // Apply theme
  applyTheme(settings.interface.theme);
  
  // Apply font size
  applyFontSize(settings.interface.fontSize);
  
  // Apply reduced motion
  if (settings.interface.reducedMotion) {
    document.body.classList.add('reduced-motion');
  } else {
    document.body.classList.remove('reduced-motion');
  }
  
  // Apply high contrast
  if (settings.interface.highContrast) {
    document.body.classList.add('high-contrast');
  } else {
    document.body.classList.remove('high-contrast');
  }
}

/**
 * Apply Theme
 */
function applyTheme(theme) {
  document.body.className = document.body.className.replace(/theme-\w+/g, '');
  
  if (theme === 'system') {
    // Use system preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.body.classList.add(prefersDark ? 'theme-dark' : 'theme-light');
  } else {
    document.body.classList.add(`theme-${theme}`);
  }
}

/**
 * Apply Font Size
 */
function applyFontSize(fontSize) {
  document.body.className = document.body.className.replace(/font-size-\w+/g, '');
  document.body.classList.add(`font-size-${fontSize}`);
}

/**
 * Update Save Status
 */
function updateSaveStatus(status, message = '') {
  const statusEl = document.getElementById('settings-status');
  if (!statusEl) return;
  
  statusEl.className = `settings-status ${status}`;
  
  switch (status) {
    case 'loading':
      statusEl.textContent = 'Loading settings...';
      break;
    case 'saving':
      statusEl.textContent = 'Saving...';
      break;
    case 'saved':
      statusEl.textContent = message || 'All changes saved';
      break;
    case 'unsaved':
      statusEl.textContent = 'You have unsaved changes';
      break;
    case 'error':
      statusEl.textContent = message || 'Error occurred';
      break;
    default:
      statusEl.textContent = message;
  }
}

/**
 * Export User Data
 */
async function exportUserData(format) {
  if (format === 'pdf') {
    await exportBudgetPDF();
  }
}

async function exportExpensesCSV(fromDateStr, toDateStr) {
  const user = getCurrentUser();
  if (!user) return;

  if (!fromDateStr || !toDateStr) {
    updateSaveStatus('error', 'Please select a date range.');
    return;
  }

  const fromDate = new Date(fromDateStr);
  const toDate = new Date(toDateStr);
  toDate.setHours(23, 59, 59, 999);

  if (fromDate > toDate) {
    updateSaveStatus('error', 'Start date must be before end date.');
    return;
  }

  updateSaveStatus('loading');

  const EXPENSE_LIMIT = 10000;

  try {
    const expensesQuery = query(
      collection(db, `users/${user.uid}/expenses`),
      where('date', '>=', Timestamp.fromDate(fromDate)),
      where('date', '<=', Timestamp.fromDate(toDate)),
      orderBy('date', 'desc'),
      limit(EXPENSE_LIMIT)
    );

    const snapshot = await getDocs(expensesQuery);
    const expenses = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    if (expenses.length === 0) {
      updateSaveStatus('error', 'No expenses found in that date range.');
      return;
    }

    const escapeCSV = val => {
      const s = String(val ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const headers = ['Date', 'Description', 'Category', 'Amount', 'Is Return', 'Is Recurring'];
    const rows = expenses.map(e => {
      const date = e.date?.toDate ? e.date.toDate() : new Date(e.date);
      return [
        date.toLocaleDateString('en-US'),
        escapeCSV(e.description || ''),
        escapeCSV(e.category || ''),
        (parseFloat(e.amount) || 0).toFixed(2),
        e.isReturn ? 'Yes' : 'No',
        e.isRecurring ? 'Yes' : 'No'
      ].join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `budgley-expenses-${fromDateStr}-to-${toDateStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    const limitNote = expenses.length === EXPENSE_LIMIT ? ` (limit of ${EXPENSE_LIMIT.toLocaleString()} reached)` : '';
    updateSaveStatus('success', `Exported ${expenses.length.toLocaleString()} expenses${limitNote}`);
  } catch (err) {
    console.error('CSV export error:', err);
    updateSaveStatus('error', 'Export failed. Please try again.');
  }
}

async function svgElementToPngDataUrl(svgEl) {
  const svgData = new XMLSerializer().serializeToString(svgEl);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = svgEl.viewBox?.baseVal?.width || svgEl.getBoundingClientRect().width || 300;
      canvas.height = svgEl.viewBox?.baseVal?.height || svgEl.getBoundingClientRect().height || 300;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(); };
    img.src = url;
  });
}

async function exportBudgetPDF() {
  const data = captureBudgetData();
  if (!data) {
    updateSaveStatus('error', 'No budget data to export.');
    return;
  }

  updateSaveStatus('loading');

  const totals = calculateDataTotals(data);
  const versionName = getCurrentVersionName() || 'Budget';
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });

  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN = 48;
  const COL_R = PAGE_W - MARGIN;
  const LINE_H = 16;
  let y = MARGIN;

  const fmt = (n) => '$' + (parseFloat(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // ── Logo ─────────────────────────────────────────────────────────────────
  try {
    const resp = await fetch('/assets/logos/budgley-logo.png');
    const blob = await resp.blob();
    const logoDataUrl = await new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
    const LOGO_H = 36;
    const LOGO_W = 120;
    doc.addImage(logoDataUrl, 'PNG', MARGIN, y, LOGO_W, LOGO_H);
    y += LOGO_H + 20;
  } catch {
    // fallback to text if logo fails
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(40);
    doc.text('Budgley', MARGIN, y + 20);
    y += 36;
  }

  // ── Header ──────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), COL_R, y - 10, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(40);
  doc.text(versionName, MARGIN, y);
  y += LINE_H * 1.8;

  doc.setDrawColor(200);
  doc.line(MARGIN, y, COL_R, y);
  y += LINE_H;

  // ── Income ───────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(40);
  doc.text('INCOME', MARGIN, y);
  y += LINE_H * 1.2;

  const FREQ_LABELS = { monthly: 'Monthly', weekly: 'Weekly', 'bi-weekly': 'Bi-weekly', quarterly: 'Quarterly', annual: 'Annual', 'one-time': 'One-time' };

  (data.income || []).forEach(item => {
    const label = item.label || item.name || '';
    const freq = FREQ_LABELS[item.frequency] || item.frequency || '';
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60);
    doc.text(label, MARGIN + 8, y);
    doc.setTextColor(120);
    doc.text(freq, MARGIN + 200, y);
    doc.setTextColor(60);
    doc.text(fmt(item.amount), COL_R, y, { align: 'right' });
    y += LINE_H;
  });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(40);
  doc.text('Total Income', MARGIN + 8, y);
  doc.text(fmt(totals.income), COL_R, y, { align: 'right' });
  y += LINE_H * 1.8;

  // ── Expenses ─────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('EXPENSES', MARGIN, y);
  y += LINE_H * 1.2;

  (data.categories || []).forEach(cat => {
    if (y > doc.internal.pageSize.getHeight() - 80) {
      doc.addPage();
      y = MARGIN;
    }

    // Category name
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(40);
    doc.text(cat.name || 'Unnamed', MARGIN, y);
    y += LINE_H;

    // Items
    (cat.items || []).forEach(item => {
      const label = item.label || item.name || '';
      const freq = FREQ_LABELS[item.frequency] || item.frequency || '';
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(80);
      doc.text(label, MARGIN + 16, y);
      doc.setTextColor(140);
      doc.text(freq, MARGIN + 210, y);
      doc.setTextColor(80);
      doc.text(fmt(item.amount), COL_R, y, { align: 'right' });
      y += LINE_H;
    });

    y += LINE_H * 0.4;
  });

  // ── Summary ──────────────────────────────────────────────────────────────
  if (y > doc.internal.pageSize.getHeight() - 100) {
    doc.addPage();
    y = MARGIN;
  }

  y += LINE_H * 0.4;
  doc.setDrawColor(200);
  doc.line(MARGIN, y, COL_R, y);
  y += LINE_H * 1.2;

  const summaryRows = [
    ['Total Income', totals.income],
    ['Total Expenses', totals.expenses],
    ['Net', totals.net],
  ];

  summaryRows.forEach(([label, val]) => {
    const isNet = label === 'Net';
    doc.setFont('helvetica', isNet ? 'bold' : 'normal');
    doc.setFontSize(10);
    doc.setTextColor(isNet ? 40 : 80);
    doc.text(label, MARGIN + 8, y);
    if (isNet) {
      doc.setTextColor(val >= 0 ? 34 : 192, val >= 0 ? 139 : 57, val >= 0 ? 34 : 43);
    }
    doc.text(fmt(val), COL_R, y, { align: 'right' });
    y += LINE_H * 1.3;
  });

  if (totals.savingsRate != null) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(`Savings rate: ${(totals.savingsRate * 100).toFixed(1)}%`, MARGIN + 8, y);
    y += LINE_H * 2;
  }

  // ── Donut Chart (offscreen render) ───────────────────────────────────────
  try {
    // Compute per-category monthly totals from captured data
    const categoryTotals = {};
    (data.categories || []).forEach(cat => {
      const catTotal = (cat.items || []).reduce((sum, item) => {
        return sum + convertToMonthlyAmount(parseFloat(item.amount) || 0, item.frequency || 'monthly');
      }, 0);
      if (catTotal > 0) categoryTotals[cat.name] = catTotal;
    });
    const totalIncome = totals.income;
    const totalExpenses = totals.expenses;

    const offscreen = document.createElement('div');
    offscreen.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:200px;height:200px;';
    document.body.appendChild(offscreen);
    renderDonutChartToElement(offscreen, categoryTotals, totalExpenses, totalIncome);

    const svgEl = offscreen.querySelector('svg');
    if (svgEl) {
      const chartPng = await svgElementToPngDataUrl(svgEl);
      const CHART_SIZE = 200;
      const chartX = (PAGE_W - CHART_SIZE) / 2;

      if (y + CHART_SIZE + 40 > doc.internal.pageSize.getHeight() - MARGIN) {
        doc.addPage();
        y = MARGIN;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(80);
      doc.text('Budget Allocation', PAGE_W / 2, y, { align: 'center' });
      y += LINE_H;
      doc.addImage(chartPng, 'PNG', chartX, y, CHART_SIZE, CHART_SIZE);
      y += CHART_SIZE + 8;
    }

    document.body.removeChild(offscreen);
  } catch {
    // skip chart if render fails
  }

  // ── Save ─────────────────────────────────────────────────────────────────
  const filename = `budgley-${versionName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
  updateSaveStatus('success', 'PDF exported successfully');
}

/**
 * Action Button Handlers
 */
async function handleChangePassword() {
  const user = getCurrentUser();
  if (!user?.email) {
    updateSaveStatus('error', 'No email address associated with this account.');
    return;
  }

  if (!confirm(`Send a password reset email to ${user.email}?`)) return;

  const result = await sendPasswordReset(user.email);
  if (result.success) {
    updateSaveStatus('success', `Password reset email sent to ${user.email}`);
  } else {
    updateSaveStatus('error', 'Failed to send reset email. Please try again.');
  }
}

async function handleDownloadData(fromDateStr, toDateStr) {
  const user = getCurrentUser();
  if (!user) return;

  if (!fromDateStr || !toDateStr) {
    updateSaveStatus('error', 'Please select a date range.');
    return;
  }

  const fromDate = new Date(fromDateStr);
  const toDate = new Date(toDateStr);
  toDate.setHours(23, 59, 59, 999);

  if (fromDate > toDate) {
    updateSaveStatus('error', 'Start date must be before end date.');
    return;
  }

  updateSaveStatus('loading');

  const EXPENSE_LIMIT = 10000;

  try {
    // Current budget
    const currentBudget = captureBudgetData();

    // All saved versions
    const versionMeta = getBudgetVersions();
    const versions = {};
    for (const v of versionMeta) {
      try {
        versions[v.id] = await loadBudgetVersionData(v.id);
      } catch (e) {
        // skip versions that fail to load
      }
    }

    // Expenses subcollection — filtered by date range
    const expensesSnap = await getDocs(query(
      collection(db, `users/${user.uid}/expenses`),
      where('date', '>=', Timestamp.fromDate(fromDate)),
      where('date', '<=', Timestamp.fromDate(toDate)),
      orderBy('date', 'desc'),
      limit(EXPENSE_LIMIT)
    ));
    const expenses = expensesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    // User settings doc
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const settings = userDoc.exists() ? userDoc.data()?.settings || {} : {};

    const exportPayload = {
      exportedAt: new Date().toISOString(),
      userId: user.uid,
      email: user.email,
      expenseDateRange: { from: fromDateStr, to: toDateStr },
      currentBudget,
      versions,
      expenses,
      settings
    };

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `budgley-export-${fromDateStr}-to-${toDateStr}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    const limitNote = expenses.length === EXPENSE_LIMIT ? ` (limit of ${EXPENSE_LIMIT.toLocaleString()} reached)` : '';
    updateSaveStatus('success', `Data exported — ${expenses.length.toLocaleString()} expenses${limitNote}`);
  } catch (err) {
    console.error('Export error:', err);
    updateSaveStatus('error', 'Export failed. Please try again.');
  }
}

function handleDeleteAccount() {
  console.log('Delete account clicked - not implemented yet');
  if (confirm('Account deletion is not yet implemented. This feature will be available in a future update.')) {
    updateSaveStatus('info', 'Account deletion functionality coming soon');
  }
}

/**
 * Switch to Settings View
 */
export function switchToSettings() {
  // Hide all other views
  document.querySelectorAll('.single-mode, .compare-mode, .sankey-mode, .progress-view-mode, .recurring-calendar-mode, .expense-tracker-mode, .achievements-mode, .breakdown-mode').forEach(mode => {
    mode.style.display = 'none';
  });

  // Show settings view
  const settingsMode = document.getElementById('settings-mode');
  if (settingsMode) {
    settingsMode.style.display = 'block';
  }

  // Update navigation
  document.querySelectorAll('.header-btn').forEach(btn => {
    btn.classList.remove('active');
  });
  document.getElementById('settings-btn')?.classList.add('active');

  // Load settings if not already loaded
  const user = getCurrentUser();
  if (user && !settingsState.userSettings) {
    loadUserSettings(user.uid);
  }
}

/**
 * Get Current Settings
 */
export function getCurrentSettings() {
  return settingsState.userSettings;
}

/**
 * Get Setting Value
 */
export function getSetting(path) {
  if (!settingsState.userSettings) return null;
  
  return path.split('.').reduce((obj, key) => obj?.[key], settingsState.userSettings);
}

/**
 * Update Setting Value
 */
export function updateSetting(path, value) {
  if (!settingsState.userSettings) return;
  
  const keys = path.split('.');
  const lastKey = keys.pop();
  const target = keys.reduce((obj, key) => obj[key], settingsState.userSettings);
  
  if (target) {
    target[lastKey] = value;
    settingsState.userSettings.updatedAt = new Date();
    settingsState.isDirty = true;
    scheduleAutoSave();
  }
}

/**
 * Get User's Preferred Currency
 */
export function getUserCurrency() {
  return getSetting('currency') || 'USD';
}

/**
 * Get User's Preferred Theme
 */
export function getUserTheme() {
  return getSetting('interface.theme') || 'system';
}

/**
 * Check if Analytics is Opted Out
 */
export function isAnalyticsOptedOut() {
  return getSetting('privacy.analyticsOptOut') || false;
}

/**
 * Check if Marketing is Opted Out
 */
export function isMarketingOptedOut() {
  return getSetting('privacy.marketingOptOut') || false;
}

/**
 * Get User's Preferred Default View Based on Device
 */
export function getDefaultView() {
  const isMobile = window.innerWidth <= 768; // Match CSS mobile breakpoint
  return isMobile ? 
    getSetting('interface.defaultViewMobile') || 'expenses' : 
    getSetting('interface.defaultViewDesktop') || 'budget';
}

/**
 * Check if Current Device is Mobile
 */
export function isMobileDevice() {
  return window.innerWidth <= 768;
}